import React, { useState, useEffect, useMemo } from 'react';
import {
  bandasAPI,
  avalesAPI } from '../services/api';
import { buildSections,
  prepareData } from './microsite/micrositeUtils';
import MicrositeModerno from './microsite/MicrositeModerno.jsx';
import MicrositeClasico from './microsite/MicrositeClasico.jsx';
import MicrositeElegante from './microsite/MicrositeElegante.jsx';
import MicrositeAurora from './microsite/MicrositeAurora.jsx';
import MicrositeEditorial from './microsite/MicrositeEditorial.jsx';
import MicrositePulso from './microsite/MicrositePulso.jsx';
import MicrositeVortex from './microsite/MicrositeVortex.jsx';
import MicrositeGlass from './microsite/MicrositeGlass.jsx';
import MicrositeKinetic from './microsite/MicrositeKinetic.jsx';
import {
  VStack,
  Heading,
  Text,
  Link,
  Spinner,
  Center,
} from '@chakra-ui/react';

const BandMicrosite = ({ slug }) => {
  const [bandaData, setBandaData] = useState(null);
  const [avales, setAvales] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await bandasAPI.getBySlug(slug);
        const data = response.data;
        setBandaData(data);
        const [avalesRes, statsRes] = await Promise.all([
          avalesAPI.getPublicosBanda(data.id).catch(() => ({ data: [] })),
          bandasAPI.getStats(data.id).catch(() => ({ data: null })),
        ]);
        setAvales(avalesRes.data || []);
        setStats(statsRes.data || null);
      } catch (err) {
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };
    if (slug) loadData();
  }, [slug]);

  const {
    msConfig,
    contacto,
    social,
    ensayosPublicos,
    eventosPublicos,
    actuacionesPublicas,
    locationText,
    hasSocials,
    primaryColor,
    secondaryColor,
    surfaceColor,
  } = useMemo(() => (bandaData ? prepareData(bandaData) : {}), [bandaData]);

  const sections = useMemo(
    () => buildSections(bandaData, msConfig || {}, avales, eventosPublicos || [], ensayosPublicos || []),
    [bandaData, msConfig, avales, eventosPublicos, ensayosPublicos],
  );

  if (loading) {
    return (
      <Center minH="100vh" bg="gray.50">
        <VStack gap={4}>
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
          <Text color="gray.500" fontSize="sm">Cargando...</Text>
        </VStack>
      </Center>
    );
  }

  if (error === 'not_found') {
    return (
      <Center minH="100vh" bg="gray.50">
        <VStack gap={4} textAlign="center" p={8}>
          <Heading size="2xl" color="gray.300" fontWeight="800">404</Heading>
          <Text fontSize="lg" color="gray.500">Este micrositio no existe</Text>
          <Link href="https://cadencya.com" color="purple.500" fontWeight="600">Ir a Cadencya</Link>
        </VStack>
      </Center>
    );
  }

  if (error || !bandaData) {
    return (
      <Center minH="100vh" bg="gray.50">
        <VStack gap={4} textAlign="center" p={8}>
          <Text fontSize="lg" color="gray.500">Error al cargar el micrositio</Text>
          <Link href="https://cadencya.com" color="purple.500" fontWeight="600">Ir a Cadencya</Link>
        </VStack>
      </Center>
    );
  }

  const commonProps = {
    bandaData, avales, stats, sections,
    msConfig, contacto, social,
    ensayosPublicos, eventosPublicos, actuacionesPublicas,
    locationText, hasSocials, primaryColor, secondaryColor, surfaceColor,
  };

  const tema = msConfig.tema || 'moderno';

  switch (tema) {
    case 'clasico':
      return <MicrositeClasico {...commonProps} />;
    case 'elegante':
      return <MicrositeElegante {...commonProps} />;
    case 'aurora':
      return <MicrositeAurora {...commonProps} />;
    case 'editorial':
      return <MicrositeEditorial {...commonProps} />;
    case 'pulso':
      return <MicrositePulso {...commonProps} />;
    case 'vortex':
      return <MicrositeVortex {...commonProps} />;
    case 'glass':
      return <MicrositeGlass {...commonProps} />;
    case 'kinetic':
      return <MicrositeKinetic {...commonProps} />;
    default:
      return <MicrositeModerno {...commonProps} />;
  }
};

export default BandMicrosite;
